import XRegExp from 'xregexp'

export const TIMESTAMP = { dev: 'timestamp', pub: 'published', prod: 'approved' }
export const NAMES = {
  'timestamp': 'development',
  'published': 'published',
  'approved': 'production',
}

/* a function for automatic comparison of list of items
  - name: both the id field and the displayed name of errors
  - items: an array of items
  - others: an array of comparison items
  - query: the current view and compare parameters of the page
*/
export function comparedItems (name, items, others, query) {
  // the field to check for each version
  let newt = TIMESTAMP[query.view || 'dev']
  let oldt = TIMESTAMP[query.compare || 'dev']

  let results = {}
  for (let item of items || []) {
    let status, explanation, special = item[name].length !== 32
    /* get status and explanation for the item version */
    // there is a compare, status is about comparing
    // let's temporarily assume that the item is only in view
    if (others) {
      status = 'created'
      explanation = name + ' only in ' + NAMES[newt]
    // ther is no compare, status is about dev
    // doesn't exist in development
    } else if (!item.timestamp) {
      // not a special, so has been deleted
      if (!special) {
        status = 'deleted'
        explanation = name + ' deleted in development'
      // a special, but defined in another version
      } else if (item.published || item.approved) {
        status = 'edited'
        explanation = 'special ' + name + ' with default value in development'
      // a special with just default value
      } else {
        status = null
        explanation = 'special ' + name + ' with default value'
      }
    // only in development
    } else if (!item.published && !item.approved) {
      // not a psecial, created in development
      if (!special) {
        status = 'created'
        explanation = name + ' created in development'
      // a special, has default value in other versions
      } else {
        status = 'edited'
        explanation = 'special ' + name + ' edited in development'
      }
    // development version is different form other view, edited
    } else if (item.timestamp !== (newt === 'timestamp' ? item.published || item.approved : item[newt])) {
      status = 'edited'
      explanation = (special ? 'special ' : '') + name + ' edited in development'
    // exactly the same everywhere
    } else {
      status = null
      explanation = (special ? 'special ' : '') + name + ' synchronized'
    }
    // build the object for display
    results[item[name]] = {
      [name]: item[name],
      first: {
        ...item,
        status,
        explanation,
        source: others ? 'new' : 'same', // assume this is not in other
      },
      special,
      query: { view: query.view }, // the query parameters for the link
    }
  }

  // the view argument of items that are only in compare
  let compare = query.compare
  if (compare === 'dev') compare = undefined
  // get every compare items
  for (let item of others || []) {
    let obj = results[item[name]]
    // item is also in view, update it
    if (obj) {
      obj.query.compare = query.compare
      // both versions are identical
      if (obj.first[newt] === obj.first[oldt]) {
        obj.first.status = null
        obj.first.explanation = (obj.special ? 'special ' : '') + name + ' synchronized'
        obj.first.source = 'same'
      // versions are different
      } else {
        obj.first.status = 'edited'
        obj.first.explanation = (obj.special ? 'special ' : '') + name + ' different in ' + NAMES[newt]
        // versions have different name, store the second one in second
        if (obj.first.name !== item.name) {
          obj.second = {
            ...item,
            status: 'edited',
            explanation: (obj.special ? 'special ' : '') + name + ' different from ' + NAMES[newt],
            source: 'old',
          }
        // versions have the same name
        } else {
          obj.first.source = 'same'
        }
      }
      continue
    }
    // the item is only in compare, save it
    results[item[name]] = {
      [name]: item[name],
      first: {
        ...item,
        status: 'deleted',
        explanation: name + ' absent in ' + NAMES[newt],
        source: 'old',
      },
      special: false,
      query: { view: query.compare },
    }
  }
  return results
}

const NAME = {
  order (a, b) {
    return (a.name > b.name) - (a.name < b.name)
  },
  name (item) { return item.name },
  node (name) { return { name } },
}
const FIRST_NAME = {
  order (a, b) {
    return (a.first.name > b.first.name) - (a.first.name < b.first.name)
  },
  name (item) { return item.first.name },
  node (name) { return { first: { name } } },
}

/* Turns an array of items into a tree
  - items: an array of items, item.first.name is its name
  - name: a function to get the name of an item
  - children: an optional {parentid: [intentids]} dict, for specific children
  - idfield: the name of the field to use for detecting children
*/
export function buildTree (items, first, children, idfield) {
  let order, name, node
  ({ order, name, node } = first ? FIRST_NAME : NAME)
  children = children || {}
  idfield = idfield || ''
  // sort all items by name
  items.sort(order)
  // the stach for building the tree, first item is the root
  let stack = [{ children: [], first: {} }]
  // recursively look in children
  function recurseChildren (parent) {
    let ch = children[parent[idfield] || '']
    if (ch && ch.length) {
      // sort childrend and set parent
      ch.sort(order)
      ch.forEach(child => {
        child.parent = parent.first
        recurseChildren(child)
      })
      // combine withe children
      if (parent.children) parent.children.splice(0, 0, ...ch)
      else parent.children = ch.slice()
    }
  }
  // removes the last element from the stack,
  // and adds it to its parent children
  function unstack () {
    let popped = stack.pop()
    // a normal item
    if (popped.type !== 'node') {
      // get extra children
      recurseChildren(popped)
    // a node with only one child is removed
    } else if (popped.children.length === 1) {
      popped = popped.children[0]
    }
    // add it to parent children
    stack[stack.length - 1].children.push(popped)
  }
  for (let item of items) {
    // split the path by "." and stack each item
    let path = name(item).split('.'), i = 0
    // find the first uncommon part of the name
    for (; i < path.length - 1; i++) {
      if (i + 1 >= stack.length || stack[i + 1].suffix !== path[i]) {
        break
      }
    }
    // unstack everything further
    while (i + 1 < stack.length) unstack()
    // stack everything prefix as a node
    for (; i < path.length - 1; i++) {
      let n = name(stack[stack.length - 1])
      n = (n ? n + '.' : '') + path[i]
      stack.push({
        type: 'node',
        suffix: path[i],
        [idfield]: n,
        ...node(n),
        children: [],
      })
    }
    // stack the item itself
    item.suffix = path[i]
    item.children = []
    stack.push(item)
  }
  // unstack everything and return the children of the root
  while (stack.length > 1) unstack()

  function shortenNames (children, prefix) {
    if (!children) return
    for (let child of children) {
      child.prefix = prefix
      let n = name(child), p = prefix
      if (child.type === 'node') {
        p = n + '.'
        child.shortName = n + '…'
      } else if (prefix && n.substr(0, prefix.length) === prefix) {
        child.shortName = '…' + n.substr(prefix.length)
      } else {
        child.shortName = n
      }
      shortenNames(child.children, p)
    }
    return children
  }
  return shortenNames(stack[0].children)
}

const NON_LETTER = new XRegExp('[^\\p{L}\\p{M}0-9]', 'ug')

export function filterTree (query, tree, first) {
  /* Filtering function for trees */
  let name = first ? FIRST_NAME.name : NAME.name
  query = query.toLowerCase().replace(NON_LETTER, '')
  function req (tree) {
    let out = []
    for (let child of tree) {
      let filter = (name(child) || '').toLowerCase().replace(NON_LETTER, '')
      if (filter.indexOf(query) >= 0) {
        out.push({
          ...child,
        })
      } else if ((child.children || []).length) {
        let children = req(child.children)
        if (children.length > 1) {
          out.push({
            ...child,
            children: children,
          })
        } else if (children.length === 1) {
          let newchild = children[0]
          if (newchild.shortName[0] === '…' &&
              child.shortName[child.shortName.length - 1] === '…') {
            out.push({
              ...newchild,
              shortName: child.shortName.substr(0, child.shortName.length - 1) +
                '.' + newchild.shortName.substr(1),
            })
          } else {
            out.push(newchild)
          }
        }
      }
    }
    return out
  }
  return req(tree)
}
